import React, { useEffect, useState } from "react";
import { usePhrase } from "@/contexts/LanguageContext";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, CheckCircle2 } from "lucide-react";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";

const DOC_TYPES = ["PAN", "Aadhaar (masked)", "Photograph", "Udyam certificate", "GST certificate", "ITR", "Audited financial statements", "GST returns", "Bank statements", "Project report", "Machinery quotation", "Property title", "Category certificate", "Domicile certificate"];

export default function Documents() {
  const pt = usePhrase();
  const [docs, setDocs] = useState([]);
  const [biz, setBiz] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState(DOC_TYPES[0]);

  const load = async () => { setDocs((await api.get("/documents")).data); };
  useEffect(() => { (async () => { const b = await api.get("/businesses"); setBiz(b.data[0]); load(); })(); }, []);

  const upload = async (file) => {
    if (!file || !biz) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const content_base64 = reader.result?.toString().split(",")[1];
      try {
        await api.post("/documents/upload", {
          business_id: biz.id, document_type: docType, filename: file.name,
          size: file.size, mime: file.type, content_base64,
        });
        toast.success(pt("Uploaded"));
        await load();
      } catch (e) { toast.error(pt("Upload failed")); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 brand-heading">{pt("Documents")}</h1>
        <p className="text-slate-600 mt-1 text-sm">{pt("Upload identity, business, financial and scheme documents. Auto-verified in demo mode.")}</p>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white flex flex-wrap items-center gap-3">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type-select" className="rounded-md border border-slate-300 px-3 py-2 text-sm">
          {DOC_TYPES.map((d) => <option key={d} value={d}>{pt(d)}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold cursor-pointer">
          <UploadCloud className="w-4 h-4" />
          <span>{uploading ? pt("Uploading…") : pt("Choose file")}</span>
          <input type="file" className="hidden" data-testid="doc-file-input" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <span className="text-xs text-slate-500">{pt("Max ~2 MB. Demo mode: verification is simulated.")}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.map((d) => (
          <div key={d.id} className="p-4 rounded-md border border-slate-200 bg-white card-hover" data-testid={`doc-${d.id}`}>
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-slate-700 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate">{d.document_type}</div>
                <div className="text-xs text-slate-500 truncate">{d.filename}</div>
                <div className="text-xs text-slate-500 mt-1">Uploaded {formatDate(d.uploaded_at)}</div>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs px-2 py-0.5">
              <CheckCircle2 className="w-3 h-3" /> {d.verification_status}
            </div>
          </div>
        ))}
        {docs.length === 0 && <div className="text-sm text-slate-500">{pt("No documents uploaded yet.")}</div>}
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}
